export const BUSINESS_FUNCTIONS_MODULE_CODENAME = 'business_functions_management';

export const BF_OBJECT_KEY = 'accountinfo';
export const BS_OBJECT_KEY = 'proposalinfo';

const VIEW_ONLY_POLICY = {
  can_view: false,
  can_create: false,
  can_update: false,
  can_delete: false,
  can_view_all: false,
  can_update_all: false,
};

export function getObjectPolicy(grants, modelKey) {
  return grants?.objects?.[modelKey] || {};
}

/** Module access — view capability on business functions. */
export function hasBfViewCapability(grants) {
  return Boolean(getObjectPolicy(grants, BF_OBJECT_KEY).can_view);
}

export function hasBsViewCapability(grants) {
  return Boolean(getObjectPolicy(grants, BS_OBJECT_KEY).can_view);
}

export function hasResourceScopeSelection(grants) {
  const resources = grants?.resources || {};
  const accounts = resources.accounts || [];
  const campaigns = resources.campaigns || [];
  return accounts.length > 0 || campaigns.length > 0;
}

function hasScopedViewInResources(resources = {}) {
  const accounts = resources.accounts || [];
  const campaigns = resources.campaigns || [];
  return accounts.some((entry) => Boolean(entry.can_view || entry.can_edit))
    || campaigns.some((entry) => Boolean(entry.can_view || entry.can_edit));
}

/** Whether any scoped account/campaign row grants edit. */
export function hasScopedEditCapability(grants) {
  const resources = grants?.resources || {};
  const accounts = resources.accounts || [];
  const campaigns = resources.campaigns || [];
  return accounts.some((entry) => Boolean(entry.can_edit))
    || campaigns.some((entry) => Boolean(entry.can_edit));
}

/**
 * Align module object policies with scoped resource grants.
 * Scope-tree Edit must be reflected in objects.can_update for AuthZ v2 runtime.
 */
export function syncBusinessFunctionsObjectPolicies(grants) {
  if (!grants) return grants;

  const resources = grants.resources || {};
  const hasScopedEdit = hasScopedEditCapability(grants);
  const hasScopedView = hasScopedViewInResources(resources);

  const patchObject = (key) => ({
    ...(grants.objects?.[key] || VIEW_ONLY_POLICY),
    can_view: Boolean(hasScopedView || grants.objects?.[key]?.can_view),
    can_update: Boolean(hasScopedEdit),
  });

  return {
    ...grants,
    objects: {
      ...grants.objects,
      [BF_OBJECT_KEY]: patchObject(BF_OBJECT_KEY),
      [BS_OBJECT_KEY]: patchObject(BS_OBJECT_KEY),
    },
  };
}

export function setBusinessFunctionsModuleAccess(grants, enabled) {
  const viewPolicy = {
    ...VIEW_ONLY_POLICY,
    can_view: Boolean(enabled),
  };

  const nextGrants = {
    ...grants,
    objects: {
      ...grants.objects,
      [BF_OBJECT_KEY]: viewPolicy,
      [BS_OBJECT_KEY]: viewPolicy,
    },
    resources: enabled
      ? {
          accounts: grants.resources?.accounts || [],
          campaigns: grants.resources?.campaigns || [],
          form_fields: grants.resources?.form_fields || [],
        }
      : { accounts: [], campaigns: [], form_fields: [] },
    record_actions: enabled ? (grants.record_actions || {}) : {},
  };

  return enabled ? syncBusinessFunctionsObjectPolicies(nextGrants) : nextGrants;
}

export function fieldGrantKey(entry = {}) {
  return `${entry.form_type || 'leads_form'}:${entry.form_id}:${entry.field_key}`;
}

export function fieldGrantMap(formFields = []) {
  const map = {};
  formFields.forEach((entry) => {
    if (!entry?.form_id || !entry?.field_key) return;
    map[fieldGrantKey(entry)] = {
      can_view: Boolean(entry.can_view),
      can_edit: Boolean(entry.can_edit),
    };
  });
  return map;
}

export function upsertFieldGrant(list = [], entry, patch) {
  const formType = entry.form_type || 'leads_form';
  const next = [...list];
  const index = next.findIndex(
    (item) =>
      (item.form_type || 'leads_form') === formType &&
      item.form_id === entry.form_id &&
      item.field_key === entry.field_key
  );
  const current = index >= 0
    ? next[index]
    : {
        form_type: formType,
        form_id: entry.form_id,
        campaign_id: entry.campaign_id,
        field_key: entry.field_key,
        can_view: false,
        can_edit: false,
      };
  const updated = {
    ...current,
    form_type: formType,
    form_id: entry.form_id,
    campaign_id: entry.campaign_id,
    field_key: entry.field_key,
    can_view: patch.can_view !== undefined ? Boolean(patch.can_view) : Boolean(current.can_view),
    can_edit: patch.can_edit !== undefined ? Boolean(patch.can_edit) : Boolean(current.can_edit),
  };

  if (index >= 0) {
    next[index] = updated;
  } else {
    next.push(updated);
  }
  return next;
}

export function removeFieldGrant(list = [], entry) {
  const formType = entry.form_type || 'leads_form';
  return list.filter(
    (item) =>
      !(
        (item.form_type || 'leads_form') === formType &&
        item.form_id === entry.form_id &&
        item.field_key === entry.field_key
      )
  );
}

export function removeFieldGrantsForCampaign(formFields = [], campaignId) {
  const id = String(campaignId);
  return formFields.filter((entry) => String(entry.campaign_id) !== id);
}

/** @deprecated Kept for tests / legacy callers — prefer setBusinessFunctionsModuleAccess. */
export function applyBusinessFunctionObjectChange(grants, model, action, granted) {
  if (action !== 'can_view') {
    return grants;
  }
  return setBusinessFunctionsModuleAccess(grants, granted);
}
